import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { MarkdownWYSIWYGEditor } from '@/components/common/MarkdownWYSIWYGEditor';
import { useReextractSalesOrderDraft } from '@/api/accounts-receivable/sales-orders/useReextractSalesOrderDraft';
import { SaveNoteToMemoryBankDialog } from './SaveNoteToMemoryBankDialog';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import {
  MemoryBankNoteMetadata,
  MemoryBankSupportedEntityTypes,
} from '@erp/rpc';
import { useInvalidateRpcQueryFn } from '@/api/rpc/client';

interface ReextractSalesOrderDraftDialogProps {
  inboundEmailId: string;
  classificationId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  metadata?: MemoryBankNoteMetadata;
  entityId: string;
  entityType: MemoryBankSupportedEntityTypes;
}

export function ReextractSalesOrderDraftDialog({
  inboundEmailId,
  classificationId,
  open,
  onOpenChange,
  onSuccess,
  metadata,
  entityId,
  entityType,
}: ReextractSalesOrderDraftDialogProps) {
  const [refreshOCR, setRefreshOCR] = useState(true);
  const [userNote, setUserNote] = useState('');
  const [lastResult, setLastResult] = useState<{
    success: boolean;
    error?: string;
  } | null>(null);
  const invalidateUsagesQuery = useInvalidateRpcQueryFn(
    (orpc) => [
      orpc.memoryBank.listUsages.key({ input: { entityId, entityType } }),
    ],
    [entityId, entityType],
  );

  const {
    reextract,
    isReextracting,
    error,
    showSaveNoteDialog,
    lastUserNote,
    dismissSaveNoteDialog,
  } = useReextractSalesOrderDraft({
    onSuccess: (result) => {
      setLastResult({
        success: result.success,
        error: result.error,
      });

      if (result.success) {
        invalidateUsagesQuery();
        onSuccess?.();
        // Close the dialog after showing success for a moment, but only if no note dialog is shown
        if (!result.userContextNote?.trim()) {
          setTimeout(() => {
            handleClose();
          }, 2000);
        }
      }
    },
    onError: (error) => {
      setLastResult({
        success: false,
        error: error.message,
      });
    },
  });

  const handleReextract = useCallback(async () => {
    setLastResult(null);

    try {
      await reextract({
        inboundEmailId,
        classificationId,
        userContextNoteMarkdown: userNote.trim() || undefined,
        refreshOCR,
      });
    } catch (err) {
      // Error is handled by the hook
    }
  }, [reextract, inboundEmailId, classificationId, userNote, refreshOCR]);

  const handleClose = useCallback(() => {
    if (!isReextracting) {
      onOpenChange(false);
      // Reset state after a delay to allow dialog animation
      setTimeout(() => {
        setUserNote('');
        setLastResult(null);
      }, 200);
    }
  }, [isReextracting, onOpenChange]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowsClockwiseIcon className="h-5 w-5" />
              Re-extract Purchase Order
            </DialogTitle>
            <DialogDescription>
              Re-run the AI extraction process for this purchase order with
              optional context.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* OCR Refresh Option */}
            <div className="flex items-center space-x-2">
              <Switch
                id="refresh-ocr"
                checked={refreshOCR}
                onCheckedChange={setRefreshOCR}
                disabled={isReextracting}
              />
              <Label htmlFor="refresh-ocr" className="text-sm">
                Redo document text extraction
              </Label>
            </div>

            <p className="text-sm text-muted-foreground">
              {refreshOCR
                ? 'Will re-process attachments text extraction, and then rerun AI matching.'
                : 'Will use existing attachment text extraction and only rerun AI matching.'}
            </p>

            {/* User Context Note Section */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Optional Context Note (Markdown)
              </Label>
              <p className="text-sm text-muted-foreground">
                Provide additional context to help the AI with extraction
                accuracy.
              </p>

              <div className="space-y-2">
                <MarkdownWYSIWYGEditor
                  initialValue={userNote}
                  onChange={setUserNote}
                  placeholder="Provide context to help with extraction (e.g., customer naming conventions, product details, etc.). Or explain what went wrong with the previous extraction."
                  height={150}
                  showToolbar
                />
              </div>
            </div>

            {/* Result Display */}
            {lastResult && (
              <AlertContainer
                variant={lastResult.success ? 'default' : 'destructive'}
              >
                <AlertTitle>
                  {lastResult.success ? 'Success' : 'Error'}
                </AlertTitle>
                <AlertDescription>
                  {lastResult.success
                    ? 'Purchase order re-extracted successfully!'
                    : `Re-extraction failed: ${lastResult.error}`}
                </AlertDescription>
              </AlertContainer>
            )}

            {/* Error Display */}
            {error && !lastResult && (
              <AlertContainer variant="destructive">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error.message}</AlertDescription>
              </AlertContainer>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isReextracting}
            >
              {lastResult?.success ? 'Close' : 'Cancel'}
            </Button>
            <Button onClick={handleReextract} disabled={isReextracting}>
              {isReextracting ? (
                <>
                  <ArrowsClockwiseIcon className="h-4 w-4 mr-2 animate-spin" />
                  Re-extracting...
                </>
              ) : (
                'Re-extract'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Note to Memory Bank Dialog */}
      <SaveNoteToMemoryBankDialog
        open={showSaveNoteDialog}
        onOpenChange={() => {}} // Controlled by hook
        note={lastUserNote}
        metadata={metadata}
        entityId={entityId}
        entityType={entityType}
        onSaveNoteToMemoryBank={() => {
          dismissSaveNoteDialog();
          // Close the main dialog after saving note
          handleClose();
        }}
        onSkipSaving={() => {
          dismissSaveNoteDialog();
          // Close the main dialog after skipping note save
          handleClose();
        }}
      />
    </>
  );
}
